import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { api } from '@orisini/ui/lib/api'

interface Unduhan {
  version: string
  filename: string
  url: string
  sizeBytes: number | null
}

interface Rilis {
  windows: Unduhan | null
  mac: Unduhan | null
  macAutoUpdate: boolean
}

function ukuran(bytes: number | null): string {
  if (!bytes) return ''
  return ` · ${(bytes / 1_048_576).toFixed(0)} MB`
}

/**
 * Halaman unduh Orisini Agent.
 *
 * Publik: calon user harus bisa melihat aplikasinya sebelum punya akun.
 * Versinya datang dari manifes rilis di R2, jadi halaman ini tidak perlu
 * di-deploy ulang setiap kali agent dirilis.
 */
export default function DownloadPage() {
  const [rilis, setRilis] = useState<Rilis | null>(null)
  const [gagal, setGagal] = useState(false)

  useEffect(() => {
    api
      .get<Rilis>('/release')
      .then(setRilis)
      .catch(() => setGagal(true))
  }, [])

  const belumAda = rilis && !rilis.windows && !rilis.mac

  return (
    <div className="mx-auto max-w-2xl px-6 py-12 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Download Orisini Agent</h1>
        <p className="text-muted-foreground">
          A desktop app that posts from your own computer. One installation serves every Orisini
          product you're subscribed to — your platform logins never leave this computer.
        </p>
      </header>

      {gagal && (
        <p className="text-sm text-destructive">
          Couldn't load release information. Try reloading the page.
        </p>
      )}

      {belumAda && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No version has been released yet. The app is being prepared — check back later.
          </CardContent>
        </Card>
      )}

      {rilis?.windows && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Windows 10 or newer
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                v{rilis.windows.version}
                {ukuran(rilis.windows.sizeBytes)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild>
              <a href={rilis.windows.url} download>
                Download installer (.exe)
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">
              On first launch Windows shows <em>“Windows protected your PC”</em> because the
              installer isn't signed yet. Click <strong>More info</strong> then{' '}
              <strong>Run anyway</strong>. Once installed, the agent updates itself.
            </p>
          </CardContent>
        </Card>
      )}

      {rilis?.mac && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              macOS 12 or newer
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                v{rilis.mac.version}
                {ukuran(rilis.mac.sizeBytes)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild>
              <a href={rilis.mac.url} download>
                Download disk image (.dmg)
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">
              On first launch, right-click the app and choose <strong>Open</strong> — Gatekeeper
              blocks an ordinary double-click because the app isn't signed yet.
              {!rilis.macAutoUpdate && (
                <>
                  {' '}
                  The macOS version <strong>can't update itself</strong>; come back to this page to
                  get a new version.
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Before installing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Google Chrome must be installed.</strong> The agent
            uses real Chrome, not a bundled browser, so your session looks like ordinary use.
          </p>
          <p>
            After installing, sign in once with your orisini.com account. The credentials are
            exchanged for a device token and <strong className="text-foreground">not stored</strong>.
          </p>
          <p>
            Leave the computer on so the queue keeps running. Installed devices can be viewed and
            revoked in <Link to="/devices" className="underline">Devices</Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
